/**
 * Security utilities for notification system
 * Provides input validation, sanitization, and XSS protection
 */

import DOMPurify from 'dompurify';

interface ValidationResult<T = string> {
  isValid: boolean;
  sanitizedValue?: T;
  errors: string[];
}

const ALLOWED_NOTIFICATION_TYPES = ['success', 'error', 'info', 'warning'] as const;
type NotificationDataType = (typeof ALLOWED_NOTIFICATION_TYPES)[number];

function isNotificationDataType(value: string): value is NotificationDataType {
  return (ALLOWED_NOTIFICATION_TYPES as readonly string[]).includes(value);
}

interface SanitizedNotificationData {
  type: NotificationDataType;
  title: string;
  message?: string;
}

interface NotificationSecurityConfig {
  maxTitleLength: number;
  maxMessageLength: number;
  allowedHtmlTags: string[];
  allowedAttributes: string[];
}

class NotificationSecurity {
  private static instance: NotificationSecurity;
  private config: NotificationSecurityConfig = {
    maxTitleLength: 100,
    maxMessageLength: 500,
    allowedHtmlTags: ['b', 'i', 'em', 'strong', 'br'],
    allowedAttributes: [],
  };

  private constructor() {
    // Configure DOMPurify for notifications
    if (typeof window !== 'undefined' && DOMPurify.isSupported) {
      DOMPurify.addHook('beforeSanitizeElements', (node) => {
        // Remove any script tags or event handlers (hook receives generic
        // Nodes; only Elements carry a tagName and can be removed)
        if (node instanceof Element && ['SCRIPT', 'OBJECT', 'EMBED', 'IFRAME'].includes(node.tagName)) {
          node.remove();
        }
      });
    }
  }

  static getInstance(): NotificationSecurity {
    if (!NotificationSecurity.instance) {
      NotificationSecurity.instance = new NotificationSecurity();
    }
    return NotificationSecurity.instance;
  }

  /**
   * Validate and sanitize notification title
   */
  validateTitle(title: string): ValidationResult {
    const errors: string[] = [];

    // Check if title exists
    if (!title || typeof title !== 'string') {
      errors.push('Title is required and must be a string');
      return { isValid: false, errors };
    }

    // Trim whitespace
    const trimmedTitle = title.trim();

    // Check length
    if (trimmedTitle.length === 0) {
      errors.push('Title cannot be empty');
    } else if (trimmedTitle.length > this.config.maxTitleLength) {
      errors.push(`Title cannot exceed ${this.config.maxTitleLength} characters`);
    }

    // Check for suspicious patterns
    if (this.containsSuspiciousContent(trimmedTitle)) {
      errors.push('Title contains potentially unsafe content');
    }

    // Sanitize the title (remove all HTML for titles)
    const sanitizedTitle = this.sanitizeText(trimmedTitle, false);

    return {
      isValid: errors.length === 0,
      sanitizedValue: sanitizedTitle,
      errors,
    };
  }

  /**
   * Validate and sanitize notification message
   */
  validateMessage(message?: string): ValidationResult {
    const errors: string[] = [];

    // Message is optional
    if (!message) {
      return { isValid: true, sanitizedValue: '', errors: [] };
    }

    if (typeof message !== 'string') {
      errors.push('Message must be a string');
      return { isValid: false, errors };
    }

    // Trim whitespace
    const trimmedMessage = message.trim();

    // Check length
    if (trimmedMessage.length > this.config.maxMessageLength) {
      errors.push(`Message cannot exceed ${this.config.maxMessageLength} characters`);
    }

    // Check for suspicious patterns
    if (this.containsSuspiciousContent(trimmedMessage)) {
      errors.push('Message contains potentially unsafe content');
    }

    // Sanitize the message (allow limited HTML)
    const sanitizedMessage = this.sanitizeText(trimmedMessage, true);

    return {
      isValid: errors.length === 0,
      sanitizedValue: sanitizedMessage,
      errors,
    };
  }

  /**
   * Validate notification type
   */
  validateType(type: string): ValidationResult<NotificationDataType> {
    const errors: string[] = [];

    if (!type || typeof type !== 'string') {
      errors.push('Type is required and must be a string');
      return { isValid: false, errors };
    }

    const normalizedType = type.toLowerCase();

    if (!isNotificationDataType(normalizedType)) {
      errors.push(`Type must be one of: ${ALLOWED_NOTIFICATION_TYPES.join(', ')}`);
      return { isValid: false, errors };
    }

    return {
      isValid: true,
      sanitizedValue: normalizedType,
      errors,
    };
  }

  /**
   * Comprehensive validation for notification data
   */
  validateNotificationData(data: {
    type: string;
    title: string;
    message?: string;
  }): ValidationResult<SanitizedNotificationData> {
    const errors: string[] = [];

    // Validate type
    const typeValidation = this.validateType(data.type);
    if (!typeValidation.isValid) {
      errors.push(...typeValidation.errors);
    }

    // Validate title
    const titleValidation = this.validateTitle(data.title);
    if (!titleValidation.isValid) {
      errors.push(...titleValidation.errors);
    }

    // Validate message
    const messageValidation = this.validateMessage(data.message);
    if (!messageValidation.isValid) {
      errors.push(...messageValidation.errors);
    }

    if (
      errors.length === 0 &&
      typeValidation.sanitizedValue !== undefined &&
      titleValidation.sanitizedValue !== undefined
    ) {
      return {
        isValid: true,
        sanitizedValue: {
          type: typeValidation.sanitizedValue,
          title: titleValidation.sanitizedValue,
          message: messageValidation.sanitizedValue,
        },
        errors,
      };
    }

    return {
      isValid: false,
      errors,
    };
  }

  /**
   * Sanitize text content
   */
  private sanitizeText(text: string, allowLimitedHtml: boolean = false): string {
    if (typeof window === 'undefined' || !DOMPurify.isSupported) {
      // Server-side or unsupported browser - basic sanitization
      return this.basicSanitize(text);
    }

    if (allowLimitedHtml) {
      // Allow limited HTML tags for messages
      return DOMPurify.sanitize(text, {
        ALLOWED_TAGS: this.config.allowedHtmlTags,
        ALLOWED_ATTR: this.config.allowedAttributes,
        KEEP_CONTENT: true,
      });
    } else {
      // Strip all HTML for titles
      return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], KEEP_CONTENT: true });
    }
  }

  /**
   * Basic sanitization for server-side or fallback
   */
  private basicSanitize(text: string): string {
    return text
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  /**
   * Check for suspicious content patterns
   */
  private containsSuspiciousContent(text: string): boolean {
    const suspiciousPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /data:text\/html/gi,
      /vbscript:/gi,
      /expression\s*\(/gi,
      /@import/gi,
      /url\s*\(/gi,
    ];

    return suspiciousPatterns.some(pattern => pattern.test(text));
  }

  /**
   * Rate limiting for notification creation
   */
  private rateLimitMap = new Map<string, { count: number; resetTime: number }>();

  checkRateLimit(identifier: string, maxRequests: number = 10, windowMs: number = 60000): boolean {
    const now = Date.now();
    const userLimit = this.rateLimitMap.get(identifier);

    if (!userLimit || now > userLimit.resetTime) {
      // Reset or create new limit
      this.rateLimitMap.set(identifier, {
        count: 1,
        resetTime: now + windowMs,
      });
      return true;
    }

    if (userLimit.count >= maxRequests) {
      return false; // Rate limit exceeded
    }

    userLimit.count++;
    return true;
  }

  /**
   * Clean up old rate limit entries
   */
  cleanupRateLimits(): void {
    const now = Date.now();
    for (const [key, value] of this.rateLimitMap.entries()) {
      if (now > value.resetTime) {
        this.rateLimitMap.delete(key);
      }
    }
  }

  /**
   * Reset all rate limit tracking (primarily for tests)
   */
  resetRateLimits(): void {
    this.rateLimitMap.clear();
  }

  /**
   * Generate secure notification ID
   */
  generateSecureId(): string {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const array = new Uint8Array(16);
      window.crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }
    
    // Fallback for environments without crypto API
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }
}

export const notificationSecurity = NotificationSecurity.getInstance();
export type { ValidationResult, NotificationSecurityConfig, SanitizedNotificationData, NotificationDataType };
